import { useState } from "react";
import { Bell, BellOff, Loader2, Pencil, Play } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { mountainToViewer, scheduleText } from "./jobMeta";
import { LastRunCell } from "./LastRunCell";
import { When } from "./When";

const err = (e, d) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || d);

function Confirm({ ask, onCancel, onOk }) {
  return (
    <AlertDialog open={!!ask} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent data-testid="job-confirm-dialog">
        <AlertDialogHeader><AlertDialogTitle>{ask?.title}</AlertDialogTitle><AlertDialogDescription>{ask?.text}</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel data-testid="job-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onOk} data-testid="job-confirm-ok">{ask?.ok}</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** One scheduled job / timer: notes, schedule, last + next run, on/off, edit, run now. */
export function JobRow({ j, canEdit, onEdit, onHistory, reload }) {
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(null);
  const id = j.key.replace(/[^a-z0-9_-]/gi, "-");
  const toggle = async (enabled) => {
    setBusy(true);
    try { await api.put(`/cron/jobs/${encodeURIComponent(j.key)}`, { enabled }); toast.success(`${j.label} switched ${enabled ? "on" : "off"}`); reload(); }
    catch (e) { err(e, "Could not change it"); } finally { setBusy(false); }
  };
  const run = async () => {
    setBusy(true);
    try { await api.post(`/cron/jobs/${encodeURIComponent(j.key)}/run`); toast.success(`${j.label} started`); setTimeout(reload, 1500); }
    catch (e) { err(e, "Could not start it"); } finally { setBusy(false); }
  };
  const bell = async () => {
    setBusy(true);
    try { await api.put(`/cron/jobs/${encodeURIComponent(j.key)}`, { alerts: !j.alerts }); toast.success(`Failure alerts ${j.alerts ? "muted" : "on"} for ${j.label}`); reload(); }
    catch (e) { err(e, "Could not change the alert"); } finally { setBusy(false); }
  };
  const at = j.schedule && j.schedule.hour != null && !j.tz ? mountainToViewer(j.schedule.hour, j.schedule.minute) : null;
  const fixed = j.type === "platform" || j.locked;
  return (
    <tr className={`border-b border-slate-50 align-top text-xs ${j.enabled === false ? "bg-slate-50/70 text-slate-500" : ""}`} data-testid={`job-row-${id}`}>
      <td className="px-4 py-3">
        <div className="font-medium text-slate-800">{j.label}</div>
        <div className="font-mono text-[10px] text-slate-400">{j.key}</div>
        <p className="mt-1 max-w-[560px] text-[11px] leading-relaxed text-slate-600" data-testid={`job-notes-${id}`}>{j.notes || <span className="italic text-slate-400">No note</span>}</p>
      </td>
      <td className="px-3 py-3 whitespace-nowrap" data-testid={`job-schedule-${id}`}>
        {j.type === "platform" ? <span className="font-mono">{j.cron} ({j.timezone})</span> : scheduleText(j.schedule)}
        {j.tz && <span className="ml-1 rounded bg-sky-100 px-1 text-[10px] font-semibold text-sky-800" data-testid={`job-tz-${id}`}>{j.tz}</span>}
        {j.schedule_custom && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800">changed</span>}
        {at && <div className="text-[10px] text-slate-500" data-testid={`job-schedule-viewer-${id}`}>{at} your time</div>}
        {j.type !== "platform" && <div className="text-[11px] text-slate-500" data-testid={`job-next-${id}`}>Next: {j.enabled === false ? "switched off" : !j.next_run ? "—" : <When iso={j.next_run} className="inline-block align-top" />}</div>}
      </td>
      <LastRunCell j={j} id={id} onHistory={onHistory} />
      <td className="px-3 py-3">
        {j.type !== "platform" && (
          <Switch checked={j.enabled !== false} disabled={!canEdit || busy || fixed} data-testid={`job-toggle-${id}`}
                  onCheckedChange={(v) => (v && j.type === "timer" ? setAsk({ title: `Switch on ${j.label}?`, text: j.notes, ok: "Switch on", fn: () => toggle(true) }) : toggle(v))} />)}
      </td>
      <td className="px-3 py-3 text-right whitespace-nowrap">
        {j.type !== "platform" && (
          <Button size="icon" variant="ghost" className={`h-7 w-7 ${j.alerts ? "text-slate-500" : "text-rose-600"}`} disabled={!canEdit || busy} onClick={bell}
                  title={j.alerts ? "Failure alerts on — click to mute" : "Failure alerts muted — click to turn on"} data-testid={`job-alerts-${id}`} data-alerts={j.alerts ? "on" : "off"}>
            {j.alerts ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}</Button>)}
        {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(j)} data-testid={`job-edit-${id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
        {canEdit && j.type !== "platform" && (
          <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" disabled={busy || j.last_result === "running" || j.enabled === false}
                  title={j.enabled === false ? "Switch it on first" : undefined} data-testid={`job-run-${id}`}
                  onClick={() => setAsk({ title: `Run ${j.label} now?`, text: `It does exactly what it does on schedule: ${j.notes || "no note"}`, ok: "Run now", fn: run })}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Run now</Button>)}
        <Confirm ask={ask} onCancel={() => setAsk(null)} onOk={() => { const f = ask.fn; setAsk(null); f(); }} />
      </td>
    </tr>
  );
}
